import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import {
  cancelSession,
  endSeries,
} from "../../features/sessions/slice-extensions/lifecycle-slice-extension";
import { fetchSessionById } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import { localToday } from "../../features/sessions/utils/occurrenceDefaults";
import { ROUTES } from "../../routes";
import Button from "../button/Button";
import ModalShell from "../modal/ModalShell";

export type LifecycleDialogKind = "endSeries" | "cancel";

interface SessionLifecycleDialogProps {
  kind: LifecycleDialogKind;
  session: Session;
  onClose: () => void;
}

/**
 * Whole-session confirmations: end a recurring series on a date (history and
 * billed visits stay; reversible by editing the series end) or cancel a session
 * created by mistake (blocked while attendance is marked). The dismiss button
 * says "Keep session" so "Cancel" only ever means the destructive action here.
 */
const SessionLifecycleDialog: React.FC<SessionLifecycleDialogProps> = ({ kind, session, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [endDate, setEndDate] = useState(localToday());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEndSeries = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(endSeries({ sessionId: session.id, request: { endDate } })).unwrap();
      toast.success(t("sessions.lifecycle.seriesEndedToast"));
      dispatch(fetchSessionById(session.id));
      onClose();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? t("sessions.lifecycle.actionFailed"));
      setBusy(false);
    }
  };

  const handleCancelSession = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(cancelSession(session.id)).unwrap();
      toast.success(t("sessions.lifecycle.cancelledToast"));
      navigate(ROUTES.SESSIONS);
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? t("sessions.lifecycle.actionFailed"));
      setBusy(false);
    }
  };

  const isEndSeries = kind === "endSeries";

  return (
    <ModalShell
      onClose={onClose}
      labelledBy="session-lifecycle-title"
      describedBy="session-lifecycle-hint"
    >
      <div className="p-4 lg:p-6">
        <h3 id="session-lifecycle-title" className="text-grey-10 dark:text-grey-90">
          {isEndSeries ? t("sessions.lifecycle.endSeriesTitle") : t("sessions.lifecycle.cancelTitle")}
        </h3>
        <p id="session-lifecycle-hint" className="mt-2 text-sm text-grey-30 dark:text-grey-70">
          {isEndSeries ? t("sessions.lifecycle.endSeriesHint") : t("sessions.lifecycle.cancelHint")}
        </p>

        {isEndSeries && (
          <label className="mt-4 block">
            <span className="text-sm text-grey-30 dark:text-grey-70">
              {t("sessions.lifecycle.lastDate")}
            </span>
            <input
              type="date"
              className="input mt-1 w-full"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
        )}

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="mt-5 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {isEndSeries ? t("common.cancel") : t("sessions.lifecycle.keepSession")}
          </Button>
          {isEndSeries ? (
            <Button onClick={handleEndSeries} disabled={busy || !endDate}>
              {t("sessions.lifecycle.confirmEndSeries")}
            </Button>
          ) : (
            <Button variant="danger" onClick={handleCancelSession} disabled={busy}>
              {t("sessions.lifecycle.confirmCancel")}
            </Button>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

export default SessionLifecycleDialog;
